// ── Theme — design tokens, colour palettes, CSS variable generation ────────────
// Edit this file to change the app's visual design without touching component logic.

export const TASK_COLOURS = {
  prune:       {bg:"#e8ede2",text:"#3a5230",border:"#9ab48a"},
  feed:        {bg:"#f0e8d0",text:"#6b4e18",border:"#c4a050"},
  mulch:       {bg:"#ede0d0",text:"#6b4428",border:"#b8845a"},
  divide:      {bg:"#e4dce8",text:"#5a3a70",border:"#a07ab8"},
  water:       {bg:"#d8e8e8",text:"#1e5050",border:"#70a8a8"},
  deadhead:    {bg:"#eae0d8",text:"#6a4030",border:"#b88060"},
  "plant/sow": {bg:"#dde8dc",text:"#2e5230",border:"#80aa88"},
  note:        {bg:"#e8e4dc",text:"#605848",border:"#b0a898"},
};

export const TASK_COLOURS_DARK = {
  prune:       {bg:"#1e2a1a",text:"#88b878",border:"#3a5830"},
  feed:        {bg:"#2a2010",text:"#c8a040",border:"#503a10"},
  mulch:       {bg:"#281a10",text:"#c08050",border:"#503010"},
  divide:      {bg:"#20182a",text:"#a878c8",border:"#402860"},
  water:       {bg:"#102020",text:"#58b0b0",border:"#103a3a"},
  deadhead:    {bg:"#281810",text:"#b07058",border:"#502810"},
  "plant/sow": {bg:"#182018",text:"#70b080",border:"#1a3a20"},
  note:        {bg:"#202018",text:"#a09880",border:"#383428"},
};

// Emoji strings kept for AI prompt context only — UI uses TASK_ICON_COMPONENTS
export const TASK_ICONS = {prune:"✂️",feed:"🌱",mulch:"🍂",divide:"🔀",water:"💧",deadhead:"🌸","plant/sow":"🌿",note:"📝"};

// icon values are Lucide component name strings — resolved in App.jsx via COLOUR_TYPE_ICONS map
export const COLOUR_TYPES = {
  flower:  {label:"Flower",  icon:"Flower2"},
  foliage: {label:"Foliage", icon:"Leaf"},
  fruit:   {label:"Fruit",   icon:"Apple"},
  bark:    {label:"Bark",    icon:"TreeDeciduous"},
  other:   {label:"Other",   icon:"Sparkles"},
};

// Typography scale — rem values respect the user's OS/browser base font size.
// Phase 31.1 Plan 11 — UAT2-GAP-07 / GitHub #209 (UI-25)
// iOS Safari auto-zooms any form control rendered below 16px and does NOT
// zoom back on blur. The dedicated `input` token guarantees every form
// control renders at >= 16px without muddling the body text scale.
export const TYPOGRAPHY = {
  heading1:  "1.625rem",  // 26px — app title
  heading2:  "1.25rem",   // 20px — view/section headings
  heading3:  "1rem",      // 16px — card headings
  input:     "1rem",      // 16px — form controls (input/textarea/select); iOS zoom floor (UAT2-GAP-07)
  body:      "0.9375rem", // 15px — standard body text
  small:     "0.8125rem", // 13px — labels, badges, meta
  tiny:      "0.75rem",   // 12px — timestamps, hints
};

// Builds the CSS custom property map applied to the root div.
// All colour and typography decisions live here.
export function buildThemeVars(darkMode) {
  const colours = darkMode ? TASK_COLOURS_DARK : TASK_COLOURS;
  return {
    "--font-h1":    TYPOGRAPHY.heading1,
    "--font-h2":    TYPOGRAPHY.heading2,
    "--font-h3":    TYPOGRAPHY.heading3,
    "--font-input": TYPOGRAPHY.input,   // UAT2-GAP-07 — iOS zoom floor (>=16px on all form controls)
    "--font-body":  TYPOGRAPHY.body,
    "--font-small": TYPOGRAPHY.small,
    "--font-tiny":  TYPOGRAPHY.tiny,
    "--c-page":         darkMode?"#1a1e1a":"#f7f5f0",
    "--c-bg":           darkMode?"#252b25":"#fff",
    "--c-surface":      darkMode?"#2d342d":"#fafaf7",
    "--c-tint":         darkMode?"#1e2a1e":"#f4faf4",
    "--c-done":         darkMode?"#1a2a1a":"#f0faf0",
    "--c-text":         darkMode?"#c8d8c8":"#2c2c2c",
    "--c-text-2":       darkMode?"#8a9e8a":"#555",
    "--c-text-3":       darkMode?"#9ab89a":"#888",
    "--c-text-4":       darkMode?"#7a987a":"#aaa",
    "--c-heading":      darkMode?"#b0c8b0":"#2d5a2d",
    "--c-border":       darkMode?"#3a443a":"#e8e4dc",
    "--c-blight":       darkMode?"#2d362d":"#f0ede6",
    "--c-overdue-bg":   darkMode?"#3a2a0c":"#fff3cd",
    "--c-overdue-text": darkMode?"#d4a020":"#856404",
    "--c-upcoming-bg":  darkMode?"#252525":"#efefef",
    "--c-upcoming-text":darkMode?"#a0a0a0":"#666",
    "--c-btn-bg":       darkMode?"#354535":"#e8e4dc",
    "--c-btn-text":     darkMode?"#a0c4a0":"#555",
    "--c-danger":       darkMode?"#f87171":"#b91c1c",
    "--c-danger-text":  "#fff",
    "--c-danger-bg":    darkMode?"#2a0a0a":"#fff0f0",
    "--c-danger-border":darkMode?"#5a1a1a":"#e8a0a0",
    "--c-focus":        darkMode?"#7ecb7e":"#2d5a2d",
    "--c-overlay":      darkMode?"rgba(0,0,0,0.7)":"rgba(0,0,0,0.5)",
    "--c-nav-bg":       "#2d5a2d",
    "--c-nav-text":     "#f0ede6",
    "--c-accent-text":  "#fff",
    "--c-success":      darkMode?"#7ecb7e":"#2d6a2d",
    // Phase 31.3 Plan 01 — onboarding design tokens. Family tokens added as
    // --font-family-* (not --font-display/body) per buildThemeVars collision
    // check: src/App.jsx uses var(--font-body) as a size in 43 sites, so
    // overwriting it with a font-family string would break existing chrome.
    "--c-brand-leaf":         darkMode ? "#b0c8b0" : "#2d5a2d",
    "--c-brand-leaf-fresh":   darkMode ? "#7ecb7e" : "#5a7a4a",
    // Phase 40 Plan 02 — primary CTA tokens. Sourced from
    // tend-design-system/colors_and_type.css. Used by SignInModal,
    // SaveToCloudCard, AuthCallback, FirstSignInUploadModal.
    "--c-brand-deep":         darkMode ? "#7ecb7e" : "#2d5a2d",
    "--c-brand-mid":          darkMode ? "#5fa55f" : "#3B6D11",
    // Phase 48 — Year-view brand-hero tokens (R-8 + CONTEXT D-03 "Moonlit garden")
    "--c-hero-sky-from":       darkMode ? "#0a0e1a" : "#d8e0c4",
    "--c-hero-sky-to":         darkMode ? "#1a1e2a" : "#4a6838",
    "--c-hero-meadow-base":    darkMode ? "#4a5a48" : "#7a9560",
    "--c-hero-meadow-mid":     darkMode ? "#5a6a48" : "#4a6838",
    "--c-hero-meadow-tuft":    darkMode ? "#6a7a68" : "#3a5228",
    "--c-hero-foliage-stem":   darkMode ? "#a8b4a0" : "#3a5228",
    "--c-hero-foliage-leaf":   darkMode ? "#a8b4a0" : "#5a7a4a",
    "--c-hero-celestial":      darkMode ? "#e8e4d0" : "rgba(255,242,205,0.85)",
    "--c-hero-celestial-glow": darkMode ? "rgba(232,228,208,0.20)" : "rgba(255,220,150,0.25)",
    "--c-hero-star":           darkMode ? "#f0e8d4" : "rgba(0,0,0,0)",
    "--ease-natural":         "cubic-bezier(0.22, 1, 0.36, 1)",
    "--ease-pop":             "cubic-bezier(0.34, 1.56, 0.64, 1)",
    "--radius-xs":            "4px",
    "--radius-sm":            "8px",
    "--radius-md":            "12px",
    "--radius-lg":            "16px",
    "--radius-xl":            "20px",
    "--radius-pill":          "999px",
    "--radius-card":          "14px",
    "--radius-modal":         "20px",
    "--min-touch":            "44px",
    "--shadow-xs":            "0 1px 2px rgba(60,50,30,0.06)",
    "--shadow-sm":            "0 2px 8px rgba(60,50,30,0.06)",
    "--shadow-md":            "0 6px 16px rgba(60,50,30,0.08), 0 2px 4px rgba(60,50,30,0.04)",
    "--shadow-lg":            "0 20px 60px rgba(60,50,30,0.12), 0 2px 8px rgba(60,50,30,0.06)",
    // Phase 49 D-10 — PlantImage size ladder (lg=80px, md=56px, sm=44px).
    // Radius reuses --radius-md/sm/xs (no new radius tokens added).
    "--plant-image-size-lg":  "80px",
    "--plant-image-size-md":  "56px",
    "--plant-image-size-sm":  "44px",
    "--font-family-display":  "'Playfair Display', serif",
    "--font-family-body":     "'Lora', serif",
    ...Object.fromEntries(
      Object.entries(colours).flatMap(([type,c])=>{
        const k=type.replace(/\//g,"-");
        return[[`--cb-${k}-bg`,c.bg],[`--cb-${k}-text`,c.text],[`--cb-${k}-border`,c.border]];
      })
    ),
  };
}

